import React from "react";
import SectionHeading from "@/components/SectionHeading";
import SEO, { orgSchema } from "@/components/SEO";

const TIMELINE = [
  { year: "Vision", title: "A timeless standard, reimagined.", text: "To restore purity, authenticity and trust to the most basic and most beautiful rituals of the Indian home." },
  { year: "Launch", title: "Sanatan Dharma™, on the shelf.", text: "Premium kapoor, kumkum, kalawa, agarbatti, oil and curated kits — held to a single uncompromising standard." },
  { year: "Future", title: "Every state. Every shelf.", text: "Building a distributor and retail network that brings the brand within reach of every household, in every state of India." },
  { year: "National", title: "A national household trust.", text: "Sitting on the shelf next to the staples of every Indian kitchen — recognised, replenished, trusted." },
  { year: "Global", title: "Carried, wherever the home is.", text: "Bringing the calm of an Indian household ritual to the diaspora — Singapore, the Gulf, the UK, North America and beyond." },
];

const About = () => (
  <div data-testid="about-page">
    <SEO
      title="About — The Essence of Sanatan Dharma"
      description="Sanatan Dharma was created to bring purity, authenticity and trust back to everyday rituals — thoughtfully curated for modern Indian households while rooted in timeless tradition."
      keywords="about sanatan dharma, sanatan dharma brand, sara greenworks, indian ritual essentials brand, puja product company"
      path="/about"
      jsonLd={orgSchema}
    />
    <section className="mandala-bg border-b border-[#3B2A1A]/10" data-testid="about-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7 fade-up">
          <p className="overline mb-5">Our Story</p>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-[#3B2A1A]">
            A brand built<br/><span className="italic text-[#C89D3C] font-light">for the Indian home.</span>
          </h1>
        </div>
        <div className="lg:col-span-5 text-lg leading-relaxed text-[#5C4A3A] font-light">
          <p>We started with one question — why have the simplest, most personal items of an Indian household never received the care and consistency of a serious brand? Sanatan Dharma is our answer.</p>
        </div>
      </div>
    </section>

    <section className="py-24 lg:py-32" data-testid="about-pillars">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <SectionHeading overline="What We Stand For" title="Trust, woven into every pack." subtitle="Tradition, authenticity, quality and family — these are not marketing words. They are the brief we hold ourselves to, every single product, every single time." />
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#3B2A1A]/10 border border-[#3B2A1A]/10">
          {[
            { t: "Trust", d: "Earned, not claimed. Built one ritual at a time." },
            { t: "Tradition", d: "The right ratios. The right ingredients. The right way." },
            { t: "Indian Homes", d: "Designed for every shelf, every chowki, every prayer." },
            { t: "Authenticity", d: "No fillers. No shortcuts. No compromise." },
            { t: "Quality", d: "Premium grade. Tested. Sealed. Delivered." },
            { t: "Family Rituals", d: "Made to be passed down, like the rituals themselves." },
          ].map((v) => (
            <div key={v.t} className="bg-white p-7" data-testid={`value-${v.t}`}>
              <p className="font-serif-display text-2xl text-[#3B2A1A]">{v.t}</p>
              <p className="text-sm text-[#5C4A3A] mt-2 font-light">{v.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-24 lg:py-32 bg-[#F5EFE3] border-y border-[#3B2A1A]/10" data-testid="timeline-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading overline="The Road Ahead" title="From a single shelf, to every home." subtitle="A measured, generational plan — built on the slow, real trust of households." />
        <div className="mt-16 relative">
          <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-px bg-[#3B2A1A]/15" />
          <div className="space-y-12 lg:space-y-16">
            {TIMELINE.map((t, i) => (
              <div key={t.year} className={`relative grid lg:grid-cols-2 gap-6 lg:gap-16 items-start ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`timeline-${i}`}>
                <div className={`pl-12 lg:pl-0 ${i % 2 === 0 ? "lg:text-right lg:pr-16" : "lg:pl-16"}`}>
                  <p className="overline">{t.year}</p>
                  <h3 className="font-serif-display text-3xl text-[#3B2A1A] mt-3 leading-tight">{t.title}</h3>
                  <p className="text-sm text-[#5C4A3A] mt-3 font-light leading-relaxed max-w-md lg:inline-block">{t.text}</p>
                </div>
                <div className={`hidden lg:block ${i % 2 === 0 ? "lg:pl-16" : "lg:pr-16 lg:text-right"}`} />
                <div className="absolute left-2.5 lg:left-1/2 lg:-translate-x-1/2 top-2 w-3 h-3 bg-[#C89D3C] border-2 border-[#FBF6EC]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default About;
